import path from 'path';

import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import ListPlaceholder from 'src/components/Dashboard/ListPlaceholder';
import displayFilterBarButtons from 'src/components/Dashboard/helpers';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import { FilterBar } from 'src/components/Filter';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import ListCard from 'src/enosikit/components/List/ListCard';
import matchesActiveState from 'src/util/activeState';
import getFullAddressString from 'src/util/address';
import {
  ACTIVE_STATE, ACTIVE_STATE_ACTIVE, DEFAULT_LIST_PAGE_SIZE, LIST_FILTER_ALL, USER_TYPE_CUSTOMER,
} from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import logger from 'src/util/logger';

import PropertyListMap from './PropertyListMap';

const messages = defineMessages({
  pageTitle: {
    id: 'property.property_list.page.title',
    defaultMessage: 'Enosi - your properties',
  },
  cardLabel: {
    id: 'property.property_list.page.manage_property.card.label',
    defaultMessage: 'Manage Property',
  },
  copyLoadMore: {
    id: 'property.property_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  },
  copyNoMore: {
    id: 'property.property_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more properties to load',
  },
  paginationError: {
    id: 'property.property_list.pagination_error.message',
    defaultMessage: 'Failed to load more properties. Please try again.',
  },
});

const PropertyListFragment = graphql`
  fragment PropertyList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "PropertyListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    viewerUser {
      type
      email
      givenName
      familyName
    }
    properties(first: $count, after: $cursor)
      @connection(key: "PropertyList_properties") {
      edges {
        cursor
        node {
          uuid
          title
          active {
            start
            finish
          }
          address {
            line1
            line2
            city
            state
            country
            postcode
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

/**
 * @param {object} param
 * @param {object} param.viewer - viewer object
 * @returns {React.ReactComponentElement} - PropertyList component
 */
function PropertyList({ viewer }) {
  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(PropertyListFragment, viewer);

  const intl = useIntl();

  const { viewerUser, properties } = data || {};

  const [activeState, setActiveState] = useState(ACTIVE_STATE_ACTIVE);
  const [paginationError, setPaginationError] = useState(null);

  const pageTitle = intl.formatMessage(messages.pageTitle);
  const cardLabel = intl.formatMessage(messages.cardLabel);
  const copyLoadMore = intl.formatMessage(messages.copyLoadMore);
  const copyNoMore = intl.formatMessage(messages.copyNoMore);
  const copyPaginationErrorModalBodyText = intl.formatMessage(messages.paginationError);

  const filterBarButtons = displayFilterBarButtons(intl, activeState);

  const updateFilter = ({ group, value }) => {
    if (value === LIST_FILTER_ALL) {
      setActiveState(LIST_FILTER_ALL);
      return;
    }
    if (group === ACTIVE_STATE) {
      setActiveState(value);
    }
  };

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs breadcrumbs={[{ name: <FormattedMessage id="breadcrumbs.properties.title" defaultMessage="Properties" /> }]} />

      <FilterBar buttons={filterBarButtons} onButtonClickFunc={updateFilter} />

      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 mt-2">
        {
          properties?.edges && getNodesFromEdges(properties).filter(
            (node) => matchesActiveState(node, activeState),
          ).map((node) => {
            const { address, uuid, title } = node;

            const addr = getFullAddressString(address) ?? '';
            return (
              <div className="col mb-4" key={uuid}>
                <ListCard
                  alias={title}
                  label={cardLabel}
                  map={viewerUser?.type === USER_TYPE_CUSTOMER && address ? <PropertyListMap address={address} height="128" /> : null}
                  path={path.join('/', 'properties', uuid)}
                  subheading={addr}
                />
              </div>
            );
          })
        }
      </div>
      {paginationError && (
        <PlaceholderErrorModal
          bodyText={copyPaginationErrorModalBodyText}
          error={paginationError}
          onCancelFunc={() => setPaginationError(null)}
          onRetryFunc={loadMore}
          placeholderComponent={() => <ListPlaceholder minimal />}
          retryDisabled={isLoadingNext}
        />
      )}
      <div>
        <LoadMoreButton
          copyLoadMore={copyLoadMore}
          copyNoMore={copyNoMore}
          hasNext={hasNext}
          onClick={loadMore}
        />
      </div>
    </>
  );
}

PropertyList.propTypes = {
  viewer: PropTypes.shape({
    id: PropTypes.string,
    viewerUser: PropTypes.shape({
      type: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }),
    properties: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
};
export default PropertyList;
